import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { FaTrash, FaImage, FaLink, FaPlus, FaTimes, FaBold, FaHeading, FaAlignLeft, FaList } from 'react-icons/fa';
import { motion } from 'framer-motion';

const BlogDashboard = () => {
  const [blogs, setBlogs] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [category, setCategory] = useState('');
  const [author, setAuthor] = useState('');
  const [links, setLinks] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const blogCollection = collection(db, 'blogs');

  useEffect(() => {
    const fetchBlogs = async () => {
      const blogSnapshot = await getDocs(blogCollection);
      const blogList = blogSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setBlogs(blogList);
    };
    fetchBlogs();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(
      'https://api.imgbb.com/1/upload?key=2d072cefa31ad1abcbcd67b25e591c43',
      {
        method: 'POST',
        body: formData,
      }
    );
    const data = await response.json();
    setImage(data.data.url);
  };

  const handleAddLink = (e) => {
    e.preventDefault();
    const linkUrl = e.target.elements.link.value;
    if (linkUrl) {
      setLinks([...links, linkUrl]);
      e.target.elements.link.value = '';
    }
  };

  const removeLink = (indexToRemove) => {
    setLinks(links.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const newBlog = {
        title,
        content,
        image,
        category,
        author,
        date: new Date().toLocaleDateString(),
        links,
      };
      await addDoc(blogCollection, newBlog);
      setBlogs([...blogs, newBlog]);
      resetForm();
      setSuccessMessage('Blog post created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding blog:', error);
    }
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setImage(null);
    setCategory('');
    setAuthor('');
    setLinks([]);
  };

  const deleteBlog = async (id) => {
    await deleteDoc(doc(db, 'blogs', id));
    setBlogs(blogs.filter((blog) => blog.id !== id));
  };

  // Apply formatting function
  const applyFormatting = (formatType) => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    const range = selection.getRangeAt(0);
    
    if (!selectedText) return;

    switch (formatType) {
      case 'bold':
        document.execCommand('bold');
        break;
      case 'h1':
        document.execCommand('formatBlock', false, 'h1');
        break;
      case 'h2':
        document.execCommand('formatBlock', false, 'h2');
        break;
      case 'paragraph':
        document.execCommand('formatBlock', false, 'p');
        break;
      case 'bullet':
        document.execCommand('insertUnorderedList');
        break;
      default:
        break;
    }
  };

  const handleTextSelection = (e) => {
    const selectedText = window.getSelection().toString();
    console.log(selectedText); // You can process the selected text as needed
  };

  const renderFormattedContent = (content) => {
    // Replace markdown with appropriate HTML tags
    const formattedContent = content
      .replace(/(\*\*)(.*?)(\*\*)/g, '<b>$2</b>') // Bold text
      .replace(/(##)(.*?)(##)/g, '<h2>$2</h2>') // Heading 2
      .replace(/(###)(.*?)(###)/g, '<h3>$2</h3>') // Heading 3
      .replace(/(\*)(.*?)(\*)/g, '<i>$2</i>') // Italics (optional)
      .replace(/(\-|\d+\.)\s*(.*?)/g, '<ul><li>$2</li></ul>') // Unordered List
      .replace(/(?:\r\n|\r|\n)/g, '<br/>'); // Line breaks
  
    return formattedContent;
  };
  

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />

        <div className="max-w-7xl mx-auto px-4 py-8">
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-white mb-2">Blog Management</h1>
            <p className="text-perspective-cloud/80">Create and manage your blog posts</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Blog Form Card */}
            <motion.div
              className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl shadow-elevated overflow-hidden"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="border-b border-white/10 p-6">
                <h2 className="text-2xl font-semibold text-white">Create New Post</h2>
              </div>
              <div className="p-6">
                {successMessage && (
                  <motion.div 
                    className="mb-6 bg-perspective-sage/15 border border-perspective-sage/30 text-perspective-sage p-4 rounded-xl"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {successMessage}
                  </motion.div>
                )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Blog Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:ring-2 focus:ring-perspective-sage focus:border-transparent transition-all"
                  />

                  <div className="space-y-2">
                    <div className="flex gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
                      <button
                        type="button"
                        onClick={() => applyFormatting('bold')}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-perspective-cloud/80 hover:text-perspective-sage"
                        title="Bold"
                      >
                        <FaBold className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('h1')}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-perspective-cloud/80 hover:text-perspective-sage"
                        title="Heading 1"
                      >
                        <FaHeading className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('h2')}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-perspective-cloud/80 hover:text-perspective-sage text-sm font-bold"
                        title="Heading 2"
                      >
                        H2
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('paragraph')}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-perspective-cloud/80 hover:text-perspective-sage"
                        title="Paragraph"
                      >
                        <FaAlignLeft className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('bullet')}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-perspective-cloud/80 hover:text-perspective-sage"
                        title="Bullet List"
                      >
                        <FaList className="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      id="content-editor"
                      contentEditable={true}
                      placeholder="Content (Use the formatting tools above or markdown: **bold**, # heading, ## subheading)"
                      value={content}
                      onInput={(e) => setContent(e.target.innerHTML)}
                      onSelect={handleTextSelection}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 h-40 max-h-60 overflow-y-auto focus:outline-none focus:ring-2 focus:ring-perspective-sage focus:border-transparent transition-all"
                      style={{ minHeight: '160px' }}
                    />

                    <div className="mt-2 p-4 rounded-xl bg-white/5 border border-white/10">
                      <h3 className="text-sm font-medium mb-2 text-perspective-cloud/90">Preview:</h3>
                      <div
                        className="prose prose-invert max-w-none text-perspective-cloud/90"
                        style={{ color: '#E7F1E4' }}
                        dangerouslySetInnerHTML={{ __html: renderFormattedContent(content) }}
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors text-perspective-cloud/80 hover:text-perspective-sage">
                      <FaImage className="w-5 h-5" />
                      <span>Choose Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        required
                        className="hidden"
                      />
                    </label>
                    {image && (
                      <div className="mt-4 relative rounded-xl overflow-hidden border border-white/10">
                        <img
                          src={image}
                          alt="Preview"
                          className="h-48 w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setImage(null)}
                          className="absolute top-2 right-2 p-2 bg-perspective-carbon/90 rounded-full hover:bg-perspective-carbon transition-colors"
                        >
                          <FaTimes className="w-4 h-4 text-perspective-sage" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      required
                      className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:ring-2 focus:ring-perspective-sage focus:border-transparent transition-all"
                    />
                    <input
                      type="text"
                      placeholder="Author"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      required
                      className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:ring-2 focus:ring-perspective-sage focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <form onSubmit={handleAddLink} className="flex gap-2">
                      <input
                        type="text"
                        name="link"
                        placeholder="Add a link (optional)"
                        className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:ring-2 focus:ring-perspective-sage focus:border-transparent transition-all"
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 bg-white/10 border border-white/10 rounded-xl hover:bg-white/15 transition-colors flex items-center gap-2 text-perspective-sage"
                      >
                        <FaPlus className="w-4 h-4" />
                        Add
                      </button>
                    </form>
                    {links.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {links.map((link, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm text-perspective-cloud/80"
                          >
                            <FaLink className="w-3 h-3" />
                            <span className="truncate max-w-[200px]">{link}</span>
                            <button
                              type="button"
                              onClick={() => removeLink(index)}
                              className="hover:text-red-400 transition-colors"
                            >
                              <FaTimes className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 bg-perspective-sage text-perspective-carbon rounded-xl font-semibold hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Publishing...' : 'Publish Blog Post'}
                </button>
              </form>
            </div>
          </motion.div>

          {/* Blog List */}
          <motion.div
            className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl shadow-elevated overflow-hidden"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="border-b border-white/10 p-6">
              <h2 className="text-2xl font-semibold text-white">Your Blog Posts</h2>
            </div>
            <div className="p-6">
              {blogs.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-perspective-cloud/60">No blog posts available yet.</p>
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto space-y-4">
                  {blogs.map((blog) => (
                    <div
                      key={blog.id}
                      className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg font-semibold text-white flex-1">{blog.title}</h3>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this blog post?')) {
                              deleteBlog(blog.id);
                            }
                          }}
                          className="ml-4 p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <FaTrash className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-perspective-cloud/70 mb-3">
                        <span>By {blog.author}</span>
                        <span>•</span>
                        <span>{blog.category}</span>
                        {blog.date && (
                          <>
                            <span>•</span>
                            <span>{blog.date}</span>
                          </>
                        )}
                      </div>
                      {blog.image && (
                        <img
                          src={blog.image}
                          alt={blog.title}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                      )}
                      <div
                        className="text-sm text-perspective-cloud/80 prose prose-invert max-w-none"
                        style={{ color: '#E7F1E4' }}
                        dangerouslySetInnerHTML={{
                          __html: renderFormattedContent(blog.content).substring(0, 200) + '...',
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDashboard;
